import * as React from "react";
import {
    Box, Button, Chip, CircularProgress, Divider, Drawer,
    IconButton, Stack, TextField, Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { RichTreeView, type RichTreeViewProps, type TreeViewItemId } from "@mui/x-tree-view";
import type { AppCategory, DbCategory } from "../models/Category";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

type PickerVariant = "drawer" | "dialog";

type Props = {
    /** Flat categories for the active client */
    categories: AppCategory[] | null | undefined;
    /** Selected category id (nullable) */
    value: number | null;
    onChange: (id: number | null) => void;

    label?: string;           // label above the readonly field
    disabled?: boolean;
    required?: boolean;
    errorText?: string;
    allowClear?: boolean;     // default true
    buttonText?: string;      // default "Choose…"
    pickerVariant?: PickerVariant;
};

// ---------- Helpers ----------
type TreeNode = { id: string; label: string; children?: TreeNode[]; _id: number };

const cmp = (a?: string | null, b?: string | null) =>
    String(a ?? "").localeCompare(String(b ?? ""));

function buildTree(flat: AppCategory[]): { roots: TreeNode[]; idToNode: Map<number, TreeNode> } {
    const byId = new Map<number, TreeNode>();
    const byParent = new Map<number | null, TreeNode[]>();

    for (const c of flat) {
        const node: TreeNode = { id: String(c.id), label: c.name, _id: c.id, children: [] };
        byId.set(c.id, node);
        const k = c.parentId ?? null;
        const arr = byParent.get(k) ?? [];
        arr.push(node);
        byParent.set(k, arr);
    }

    for (const [pid, kids] of byParent.entries()) {
        if (pid !== null) {
            const p = byId.get(pid);
            if (p) p.children = kids;
        }
    }

    const roots: TreeNode[] = [];
    for (const c of flat) {
        const isRoot = c.parentId == null || !byId.has(c.parentId);
        if (isRoot) {
            const n = byId.get(c.id);
            if (n) roots.push(n);
        }
    }

    const sortRec = (ns?: TreeNode[]) => {
        if (!ns) return;
        ns.sort((a, b) => cmp(a.label, b.label));
        ns.forEach(n => sortRec(n.children));
    };
    sortRec(roots);

    return { roots, idToNode: new Map([...byId.entries()]) };
}

function makeParentIndex(flat: AppCategory[]) {
    const parent = new Map<number, number | null>();
    const label = new Map<number, string>();
    for (const c of flat) {
        parent.set(c.id, c.parentId ?? null);
        label.set(c.id, c.name);
    }
    return { parent, label };
}

function getPathLabels(id: number, parent: Map<number, number | null>, labels: Map<number, string>): string[] {
    const out: string[] = [];
    let cur: number | null | undefined = id;
    const guard = new Set<number>(); // avoid cycles
    while (cur != null && !guard.has(cur)) {
        guard.add(cur);
        out.unshift(labels.get(cur) ?? String(cur));
        cur = parent.get(cur) ?? null;
    }
    return out;
}

// filter tree by query (keeps parents of matches)
function filterTree(nodes: TreeNode[], q: string): TreeNode[] {
    const query = q.trim().toLowerCase();
    if (!query) return nodes;
    const visit = (n: TreeNode): TreeNode | null => {
        const match = n.label.toLowerCase().includes(query);
        const kids = (n.children ?? []).map(visit).filter(Boolean) as TreeNode[];
        return match || kids.length ? { ...n, children: kids.length ? kids : undefined } : null;
    };
    return nodes.map(visit).filter(Boolean) as TreeNode[];
}


// ---------- Component ----------
export default function CategoryPickerField({
    categories,
    value,
    onChange,
    label = "Category",
    disabled,
    required,
    errorText,
    allowClear = false,
    buttonText = "Choose",
    pickerVariant = "dialog",
}: Props) {


    type Item = {
        id: TreeViewItemId;      // string | number
        label: string;
        children?: Item[];
    };

    type SingleProps = RichTreeViewProps<Item, false>; 

    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState("");
    const [tempSelection, setTempSelection] = React.useState<number | null>(null);

    const toTreeId = (n: number | null): string | null =>
        n == null ? null : String(n);

    const fromTreeId = (id: string | null): number | null => {
        if (id == null) return null;
        const n = Number(id);
        return Number.isFinite(n) ? n : null;
    };



    const selectedItemsForTree: string =
        tempSelection != null ? String(tempSelection) : "";

    const onSelect: SingleProps['onSelectedItemsChange'] = (_e, idOrNull) => {
        const id = Array.isArray(idOrNull) ? idOrNull[0] ?? null : idOrNull; // safety
        const n = typeof id === 'number' ? id : id == null ? null : Number(id);
        setTempSelection(Number.isFinite(n as number) ? (n as number) : null);
    };


    //const selected: TreeViewItemId | null = tempSelection;
    const expandedIds: TreeViewItemId[] = [];

    // Build indexes/tree only when categories change
    const { roots } = React.useMemo(() => {
        if (!categories?.length) return { roots: [] as TreeNode[], idToNode: new Map<number, TreeNode>() };
        return buildTree(categories);
    }, [categories]);

    const { parentIndex, labelIndex } = React.useMemo(() => {
        if (!categories?.length) return { parentIndex: new Map<number, number | null>(), labelIndex: new Map<number, string>() };
        const idx = makeParentIndex(categories);
        return { parentIndex: idx.parent, labelIndex: idx.label };
    }, [categories]);

    const selectedId = value;
    const breadcrumb =
        selectedId != null && categories?.length
            ? getPathLabels(selectedId, parentIndex, labelIndex).join(" > ")
            : "";

    const filtered = React.useMemo(() => filterTree(roots, query), [roots, query]);

    // Expand the path to the current (or temp) selection for context
    const expandedForSelection = React.useMemo(() => {
        const target = tempSelection ?? selectedId;
        if (target == null) return [] as string[];
        const ids: string[] = [];
        let cur: number | null | undefined = parentIndex.get(Number(target)) ?? null;
        const guard = new Set<number>();
        while (cur != null && !guard.has(cur)) {
            guard.add(cur);
            ids.push(String(cur));
            cur = parentIndex.get(cur) ?? null;
        }
        return ids;
    }, [tempSelection, selectedId, parentIndex]);

    // Open drawer prepared with current selection
    const openPicker = () => {
        setTempSelection(selectedId ?? null);
        setOpen(true);
    };

    const closePicker = () => setOpen(false);

    const applySelection = () => {
        onChange(tempSelection ?? null);
        setOpen(false);
    };

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    // --- add state for controlled expansion ---
    const [expanded, setExpanded] = React.useState<string[]>([]);

    // build a fast id->parent and id->node index once
    const idToParent = React.useMemo(() => {
        const m = new Map<number, number | null>();
        categories?.forEach(c => m.set(c.id, c.parentId ?? null));
        return m;
    }, [categories]);

    const idToNode = React.useMemo(() => {
        const map = new Map<number, TreeNode>();
        const walk = (ns?: TreeNode[]) => ns?.forEach(n => { map.set(Number(n.id), n); walk(n.children); });
        walk(roots);
        return map;
    }, [roots]);

    // helper: get ancestor chain as string ids
    const ancestorsOf = React.useCallback((id: number) => {
        const out: string[] = [];
        const seen = new Set<number>();
        let cur: number | null | undefined = idToParent.get(id) ?? null;
        while (cur != null && !seen.has(cur)) {
            seen.add(cur);
            out.push(String(cur));
            cur = idToParent.get(cur) ?? null;
        }
        return out;
    }, [idToParent]);

    // find all matches in the FULL tree (not the filtered one)
    const collectMatches = React.useCallback((q: string) => {
        const query = q.trim().toLowerCase();
        if (!query) return new Set<number>();
        const hits = new Set<number>();
        idToNode.forEach((n, numId) => {
            if (n.label.toLowerCase().includes(query)) hits.add(numId);
        });
        return hits;
    }, [idToNode]);

    // compute expansion when query or selection changes
    React.useEffect(() => {
        if (!query) {
            // no search → expand the path to current selection (for context)
            const ids = expandedForSelection; // you already computed this
            setExpanded(ids);
            return;
        }
        const matches = collectMatches(query);
        const expandSet = new Set<string>();
        // expand all ancestors of each matching node (so matches are visible)
        matches.forEach(id => ancestorsOf(id).forEach(a => expandSet.add(a)));
        // optional: also expand matched nodes themselves
        matches.forEach(id => expandSet.add(String(id)));
        setExpanded(Array.from(expandSet));
    }, [query, expandedForSelection, collectMatches, ancestorsOf]);

    // keep user toggles (keyboard/chevrons) in sync
    const handleExpandedChange = (_e: any, ids: string[] | string) => {
        setExpanded(Array.isArray(ids) ? ids : [ids]);
    };

    const itemsForTree = filtered.map(n => ({
        ...n,
        id: String(n.id),              
        children: n.children?.map(c => ({ ...c, id: String(c.id) })) ?? [],
    }));

    const PickerBody = (
        <>
            <Box>
                {selectedId != null && (
                    <Box sx={{ mt: 1 }}>
                        <Chip
                            label={breadcrumb || labelIndex.get(selectedId) || String(selectedId)}
                            onDelete={allowClear ? () => onChange(null) : undefined}
                        />
                    </Box>
                )}
                    {/* Header */}
                    <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="h6" sx={{ flex: 1 }}>
                            Choose category
                        </Typography>
                        <IconButton onClick={closePicker} aria-label="Close"><CloseIcon /></IconButton>
                    </Box>
                    <Divider />

                    {/* Search */}
                    <Box sx={{ p: 2, pt: 1 }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Search categories…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                        <Typography variant="caption" sx={{ mt: 0.5, display: "block", color: "text.secondary" }}>
                            Single select. Use the tree to navigate.
                        </Typography>
                    </Box>

                    {/* Tree */}
                    <Box sx={{ px: 1, pb: 2, overflow: "auto", flex: 1 }}>
                        {!categories ? (
                            <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
                                <CircularProgress size={18} />
                                <Typography variant="body2">Loading categories…</Typography>
                            </Box>
                        ) : (
                            <RichTreeView
                                multiSelect={false}
                                items={filtered}
                                selectedItems={selectedItemsForTree}
                                onSelectedItemsChange={(_e, ids) => {
                                    const arr = Array.isArray(ids) ? ids : [ids];
                                    const first = arr[0];
                                    const num = first != null ? Number(first) : NaN;
                                    setTempSelection(Number.isFinite(num) ? num : null);
                                }}
                                defaultExpandedItems={query ? filtered.map(n => n.id) : expandedForSelection}
                                aria-label="Categories tree"
                                expandedItems={expanded}
                                onExpandedItemsChange={handleExpandedChange}
                            />
                        )}
                        {!filtered.length && categories && (
                            <Typography variant="body2" sx={{ px: 2, py: 1, color: "text.secondary" }}>
                                No matches
                            </Typography>
                        )}
                    </Box>

                    {/* Footer */}
                    <Divider />
                    <Box sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="body2">
                            {tempSelection != null
                                ? getPathLabels(Number(tempSelection), parentIndex, labelIndex).join(" › ")
                                : "No category selected"}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <Button onClick={closePicker}>Cancel</Button>
                            <Button
                                variant="contained"
                                onClick={applySelection}
                                disabled={tempSelection == null}
                            >
                                Apply
                            </Button>
                        </Box>
                    </Box>
            </Box>
        </>
    );

    return (

        <Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
                <TextField
                    label={label}
                    fullWidth
                    value={breadcrumb}
                    placeholder="No category selected"
                    InputProps={{ readOnly: true }}
                    disabled={disabled || !categories}
                    required={required}
                    error={!!errorText}
                    helperText={errorText}
                />
                <Button variant="contained" color="primary" onClick={openPicker} disabled={disabled || !categories} size="small">
                    {buttonText}
                </Button>
                {allowClear && (
                    <Button
                        variant="text"
                        onClick={() => onChange(null)}
                        disabled={disabled || selectedId == null}
                        color="primary"
                        size="small"
                    >
                        Clear
                    </Button>
                )}
            </Box>

            {pickerVariant === "drawer" ? (
                <Drawer
                    anchor="right"
                    open={open}
                    onClose={closePicker}
                    ModalProps={{ keepMounted: true }}
                    PaperProps={{ sx: { width: 440 } }}
                >
                    {PickerBody}
                </Drawer>
            ) : (
                            <Dialog
                                open={open}
                                onClose={closePicker}
                                fullWidth
                                    maxWidth="sm"
                                    fullScreen={fullScreen}
                            >
                                {PickerBody}
                            </Dialog>
            )}
        </Box>
    );
}
